import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  FileText, 
  Activity, 
  Clock, 
  CheckCircle2, 
  Sparkles, 
  ArrowUpRight, 
  RefreshCw, 
  AlertCircle,
  ChevronRight,
  Share2
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

export function UserDashboardHome() {
  const { session, user } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    total: 0,
    drafts: 0,
    inProgress: 0,
    pendingActions: 0,
    uat: 0,
    completed: 0
  });
  const [recentUseCases, setRecentUseCases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = session?.access_token || localStorage.getItem('compass_token');
      const res = await fetch('/api/user/dashboard', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        throw new Error(`Failed to load dashboard: ${res.statusText}`);
      }

      const data = await res.json();
      if (data.success) {
        setStats(data.stats || {});
        setRecentUseCases(data.recentUseCases || []);
      } else {
        throw new Error(data.error || 'Unable to fetch dashboard statistics');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [session]);

  return (
    <div className="space-y-6 w-full">
      {/* Welcome Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-900/40 via-indigo-900/30 to-purple-900/20 border border-blue-500/20 p-6 backdrop-blur-md">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-semibold mb-2 border border-blue-500/20">
              <Sparkles className="h-3.5 w-3.5" />
              <span>SOC Detection Engineering Portal</span>
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Welcome back, {user?.displayName || user?.email?.split('@')[0]}
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Track detection engineering rules across the strict 15-stage governance lifecycle. All metrics and workflow data are backed by live telemetry and Supabase records.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchDashboardData}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border border-border bg-card hover:bg-muted text-foreground transition-colors disabled:opacity-50"
            >
              <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
              <span>Refresh</span>
            </button>
            <button
              onClick={() => navigate('/user/use-cases/create')}
              className="flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-500/20 transition-all"
            >
              <span>+ Create Use Case</span>
            </button>
          </div>
        </div>

        {/* Decorative background glow */}
        <div className="absolute top-0 right-0 -mt-8 -mr-8 h-48 w-48 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />
      </div>

      {error && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 text-sm">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* METRIC CARDS (6 CARDS REQUIRED: Total, Drafts, In Progress, Pending Actions, UAT, Completed) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {/* CARD 1: TOTAL */}
        <MetricCard 
          label="Total Use Cases"
          value={stats.total}
          icon={Shield}
          color="blue"
          onClick={() => navigate('/user/use-cases')}
          loading={loading}
        />

        {/* CARD 2: DRAFTS */}
        <MetricCard 
          label="Drafts"
          value={stats.drafts}
          icon={FileText}
          color="slate"
          onClick={() => navigate('/user/use-cases/drafts')}
          loading={loading}
        />

        {/* CARD 3: IN PROGRESS */}
        <MetricCard 
          label="In Progress"
          value={stats.inProgress}
          icon={Activity}
          color="amber"
          onClick={() => navigate('/user/in-progress')}
          loading={loading}
        />

        {/* CARD 4: PENDING ACTIONS */}
        <MetricCard 
          label="Pending Actions"
          value={stats.pendingActions}
          icon={Clock}
          color="rose"
          onClick={() => navigate('/user/pending-actions')}
          highlight={stats.pendingActions > 0}
          loading={loading}
        />

        {/* CARD 5: UAT */}
        <MetricCard 
          label="UAT Soak"
          value={stats.uat}
          icon={Share2}
          color="purple"
          onClick={() => navigate('/user/in-progress')}
          loading={loading}
        />

        {/* CARD 6: COMPLETED */}
        <MetricCard 
          label="Completed"
          value={stats.completed}
          icon={CheckCircle2}
          color="emerald"
          onClick={() => navigate('/user/use-cases/completed')}
          loading={loading}
        />
      </div>

      {/* RECENT USE CASES TABLE */}
      <div className="neo rounded-[2rem] p-6 shadow-xs overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h2 className="text-sm font-bold text-foreground">Recent Use Cases</h2>
            <p className="text-xs text-muted-foreground">Detection rules authored by or assigned to your authenticated identity</p>
          </div>
          <button 
            onClick={() => navigate('/user/use-cases')}
            className="flex items-center gap-1 text-xs font-semibold text-blue-500 hover:text-blue-600 transition-colors"
          >
            <span>View All</span>
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>

        {loading ? (
          <div className="p-12 flex flex-col items-center justify-center text-muted-foreground gap-3">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
            <span className="text-xs">Fetching real Supabase records...</span>
          </div>
        ) : recentUseCases.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <Shield className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-sm font-medium text-foreground">No recent Use Cases found</p>
            <p className="text-xs mt-1">Submit your first detection engineering use case to launch the 15-stage workflow.</p>
            <button
              onClick={() => navigate('/user/use-cases/create')}
              className="mt-4 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              Create New Use Case
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted/40 text-muted-foreground uppercase text-[10px] tracking-wider border-b border-border">
                <tr>
                  <th className="py-3 px-4 font-semibold">Number</th>
                  <th className="py-3 px-4 font-semibold">Title</th>
                  <th className="py-3 px-4 font-semibold">Current Stage</th>
                  <th className="py-3 px-4 font-semibold">Status</th>
                  <th className="py-3 px-4 font-semibold">Priority</th>
                  <th className="py-3 px-4 font-semibold">SLA</th>
                  <th className="py-3 px-4 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {recentUseCases.map((uc) => (
                  <tr 
                    key={uc.id} 
                    className="hover:bg-muted/30 transition-colors cursor-pointer group"
                    onClick={() => navigate('/user/use-cases')}
                  >
                    <td className="py-3 px-4 font-mono font-bold text-blue-500 group-hover:underline">
                      {uc.number}
                    </td>
                    <td className="py-3 px-4 font-medium text-foreground max-w-xs truncate">
                      {uc.title}
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-muted text-foreground border border-border">
                        Step {uc.current_step}: {uc.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <StatusBadge status={uc.status} />
                    </td>
                    <td className="py-3 px-4">
                      <PriorityBadge priority={uc.priority} />
                    </td>
                    <td className="py-3 px-4">
                      <SlaBadge status={uc.sla_status} />
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="inline-flex items-center gap-1 text-xs text-blue-500 group-hover:text-blue-600 font-medium">
                        <span>Details</span>
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Metric Card Component
function MetricCard({ 
  label, 
  value, 
  icon: Icon, 
  color, 
  onClick, 
  highlight, 
  loading 
}: { 
  label: string; 
  value: number; 
  icon: any; 
  color: 'blue' | 'slate' | 'amber' | 'rose' | 'purple' | 'emerald';
  onClick: () => void; 
  highlight?: boolean; 
  loading?: boolean; 
}) {
  const colorMap = {
    blue: "text-blue-500 bg-blue-500/10 border-blue-500/20",
    slate: "text-slate-400 bg-slate-500/10 border-slate-500/20",
    amber: "text-amber-500 bg-amber-500/10 border-amber-500/20",
    rose: "text-rose-500 bg-rose-500/10 border-rose-500/20",
    purple: "text-purple-500 bg-purple-500/10 border-purple-500/20",
    emerald: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20"
  };

  return (
    <div 
      onClick={onClick}
      className={cn(
        "neo rounded-[2rem] p-5 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer group flex flex-col justify-between",
        highlight && "border-rose-500/40 shadow-sm shadow-rose-500/10"
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide truncate">
          {label}
        </span>
        <div className={cn("p-1.5 rounded-xl border", colorMap[color])}>
          <Icon className="h-4 w-4" />
        </div>
      </div>

      <div className="mt-3">
        {loading ? (
          <div className="h-8 w-12 rounded bg-muted animate-pulse" />
        ) : (
          <span className="text-3xl font-bold tracking-tight text-foreground group-hover:text-primary transition-colors">
            {value}
          </span>
        )}
      </div>

      <div className="mt-2 flex items-center gap-1 text-[10px] text-muted-foreground font-medium group-hover:text-foreground transition-colors">
        <span>View list</span>
        <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const isDone = status === 'PRODUCTION_DEPLOYED' || status === 'CLOSED';
  const isPending = status === 'CHANGES_REQUIRED' || status === 'LOGS_UNAVAILABLE';
  const isUat = status.includes('UAT');

  return (
    <span className={cn(
      "inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border",
      isDone && "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      isPending && "bg-rose-500/10 text-rose-500 border-rose-500/20",
      isUat && "bg-purple-500/10 text-purple-500 border-purple-500/20",
      !isDone && !isPending && !isUat && "bg-blue-500/10 text-blue-500 border-blue-500/20"
    )}>
      {status}
    </span>
  );
}

function PriorityBadge({ priority }: { priority: string }) {
  const p = (priority || 'medium').toLowerCase();
  return (
    <span className={cn(
      "inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold",
      (p === 'critical' || p === 'urgent') && "bg-rose-500/15 text-rose-500",
      p === 'high' && "bg-amber-500/15 text-amber-500",
      p === 'medium' && "bg-blue-500/15 text-blue-500",
      p === 'low' && "bg-slate-500/15 text-slate-400"
    )}>
      {priority}
    </span>
  );
}

function SlaBadge({ status }: { status?: string }) {
  const s = status || 'ON_TRACK';
  return (
    <span className={cn(
      "inline-flex items-center gap-1 text-[10px] font-semibold",
      s === 'BREACHED' && "text-rose-500",
      s === 'DUE_SOON' && "text-amber-500",
      s === 'ON_TRACK' && "text-emerald-500"
    )}>
      <span className={cn(
        "h-1.5 w-1.5 rounded-full",
        s === 'BREACHED' && "bg-rose-500",
        s === 'DUE_SOON' && "bg-amber-500 animate-ping",
        s === 'ON_TRACK' && "bg-emerald-500"
      )} />
      <span>{s.replace(/_/g, ' ')}</span>
    </span>
  );
}
